/**
 * The `Timeline` catalog component: dated events on one rule, today marked.
 *
 * 16 instances in the design (13i, 2g) and the visual a holder acts on:
 * dates in order, and which one is still in front of them.
 *
 * WHICH EVENT IS NEXT IS THE SERVER'S, NEVER `new Date()`. Each row's
 * `state` was decided against the TURN's own trading day, and this marks
 * the first one that is today or still ahead. A client reading its own
 * clock would draw a reader in another timezone a different answer from the
 * one that was written.
 *
 * WHAT THE MARKED DOT CLAIMS, AND THE ROW IT MAY NOT CLAIM IT ABOUT. This
 * file used to say the visual was "a book closure they can still reach and
 * one they cannot", and it drew exactly that: the earliest `future` row took
 * the accent ring, book closures included. Whether a book closure is still
 * reachable is the one thing the engine does not know. Entitlement follows
 * the shareholder register, a purchase reaches the register only once it
 * settles, so the last qualifying trade falls earlier than the date on the
 * dot -- and how much earlier is unsigned, which every prose surface was
 * corrected to say on 2026-09-10. A picture asserting it instead moved the
 * claim into the one channel S3 never reads.
 *
 * So such a row arrives as `deadline_earlier`, it never takes the ring, and
 * NOTHING ELSE TAKES IT IN ITS PLACE. Skipping to the next dot would answer
 * the same question with a worse answer: it would point a reader past the
 * nearest date on their calendar at one further out. An unmarked rule and a
 * said caveat is the honest shape, and the caveat is the server's caption
 * rather than a sentence typed in here.
 *
 * The dates are laid out as a vertical sequence. Long event names can wrap
 * without colliding, while the connected markers still make their order and
 * the next event clear on a narrow panel.
 *
 * The label is the server's own word for the event ("Book closure") or the
 * platform's own text for an AGM. Nothing here names anything.
 */
import { asAReaderWritesDates, captionLabel } from '@/features/mitra/lib/figures'
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { EventState } from '@/features/mitra/api/frames'
import { Label } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const TimelineApi = {
  name: 'Timeline',
  schema: z
    .object({
      events: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/events`, fixed by `to_messages`. */
export type EventPart = {
  id: string
  date: string
  label: string
  state: EventState
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const timelineApi = TimelineApi as unknown as ComponentApi

export const Timeline = createComponentImplementation<ComponentApi>(
  timelineApi,
  ({ props }: { props: { events?: unknown; caption?: unknown } }) => (
    <TimelineRule
      events={listOf<EventPart>(props.events)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/**
 * How one dot is filled, given its state and whether it is the marked one.
 *
 * DASHED IS THE WHOLE DIFFERENCE between a book closure ahead and any other
 * date ahead: an outline that is not closed reads as something unsettled,
 * which is exactly what that row is. Deliberately not the accent colour,
 * which would claim it can be reached, and deliberately not the past grey,
 * which would claim the date has gone.
 */
function dotStyle(state: EventState, marked: boolean): string {
  if (marked) return 'bg-up ring-4 ring-up/20'
  if (state === 'past') return 'bg-ink-soft'
  // `bg-surface` AND NOT `bg-card` — not because `bg-card` names nothing
  // here (it does: mm-web's globals.css maps `--color-card` to `--card`, and
  // theme-bridge.css:22 sends the widget's own `--color-surface` to that same
  // `--card`, so today the two classes resolve to the same value). This file
  // speaks the widget's own token vocabulary, and `--color-surface` is the
  // one theme-bridge.css binds — that is reason enough on its own to keep
  // `bg-surface` here. It was also load-bearing upstream: `mm-ai/web`'s
  // embed.css never defined `--color-card` at all, so `bg-card` resolved to
  // nothing there and these dots drew with no fill — the rule ran straight
  // through the two states that are supposed to read as unfilled-but-closed
  // and unsettled.
  // Same silent shape as `bg-ink-faint`, which `shapes/Comparison.tsx`
  // records: a class naming no token renders nothing and raises nothing.
  // `--color-surface` is the card this chart is drawn on, which is the fill
  // the outline was asking for; `Comparison`'s dot rings itself in the same
  // token for the same reason.
  if (state === 'deadline_earlier') {
    return 'border border-dashed border-ink-soft bg-surface'
  }
  return 'border border-ink-soft bg-surface'
}

/** The drawing, given the list the surface resolved. */
export function TimelineRule({ events, caption }: { events: EventPart[]; caption: string | null }) {
  if (events.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  // The event the reader has to act on next: the earliest one that has not
  // gone. The server decided each state against the TURN's trading day, so
  // this reads its judgement rather than a clock.
  //
  // AND NOTHING IS MARKED WHEN THAT EVENT IS A BOOK CLOSURE, because the
  // engine cannot say whether it can still be acted on. `findIndex` runs over
  // every state that is not `past` and the marker is then withdrawn, rather
  // than the row being filtered out: filtering would hand the ring to a later
  // date and point the reader past the nearer one.
  const aheadAt = events.findIndex((e) => e.state !== 'past')
  const nextAt = events[aheadAt]?.state === 'deadline_earlier' ? -1 : aheadAt

  return (
    <figure
      data-role="chart"
      data-component="timeline"
      className="m-0 flex flex-col gap-2 px-3 pb-3 pt-3"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      <ol className="m-0 flex list-none flex-col p-0">
        {events.map((e, i) => (
          <li
            key={e.id}
            data-role="event"
            data-state={e.state}
            className="mitra-timeline-grid grid min-w-0 gap-2"
          >
            <div className="flex min-h-12 flex-col items-center">
              <span className={`w-px flex-1 ${i === 0 ? '' : 'bg-edge'}`} />
              <span
                data-role="event-dot"
                data-next={i === nextAt ? 'true' : 'false'}
                className={`h-2 w-2 shrink-0 rounded-full ${dotStyle(e.state, i === nextAt)}`}
              />
              <span className={`w-px flex-1 ${i === events.length - 1 ? '' : 'bg-edge'}`} />
            </div>
            <div
              className={`mb-3 min-w-0 rounded-block px-2 py-2 ${
                i === nextAt ? 'bg-ground' : 'bg-surface'
              }`}
            >
              <span
                className={`block break-words text-body-s leading-snug ${
                  i === nextAt
                    ? 'font-medium text-ink'
                    : e.state === 'past'
                      ? 'text-ink-soft'
                      : 'text-ink'
                }`}
              >
                {e.label}
              </span>
              <span className="mt-1 block text-body-xs tabular-nums text-ink-soft">
                {asAReaderWritesDates(e.date)}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </figure>
  )
}
